import { authConfig } from "./auth.config";
import { codeChallenge, randomUrlSafe } from "./pkce";

/**
 * Browser side of the session:
 * - the 30-day refresh token lives in an HttpOnly cookie set by the API
 *   (never visible here);
 * - the 60-minute access token lives only in this module's memory and is
 *   renewed through POST /v1/auth/refresh before it expires and after reloads.
 */

/** Our API refuses session requests without this header (CSRF protection). */
const SESSION_HEADERS = { "X-MDF-Session": "1" } as const;
/** Survives only the redirect to the login page and back (same tab). */
const PENDING_LOGIN_KEY = "mdf_pending_login";
const RENEW_BEFORE_MS = 5 * 60 * 1000;
const LINKED_ON_FIRST_SIGN_IN = /already found an entry for username/i;

interface TokenResponse {
  accessToken: string;
  expiresIn: number;
}

interface PendingLogin {
  verifier: string;
  state: string;
  returnTo: string;
  googleRetried: boolean;
}

let accessToken: string | null = null;
let expiresAt = 0;
let renewTimer: ReturnType<typeof setTimeout> | undefined;
let refreshInFlight: Promise<boolean> | null = null;
const endListeners = new Set<() => void>();

const apiUrl = (path: string) => `${authConfig().apiBaseUrl}${path}`;

function setToken({ accessToken: token, expiresIn }: TokenResponse): void {
  accessToken = token;
  expiresAt = Date.now() + expiresIn * 1000;
  clearTimeout(renewTimer);
  renewTimer = setTimeout(
    () => void refreshSession(),
    Math.max(expiresIn * 1000 - RENEW_BEFORE_MS, 30_000),
  );
}

function clearToken(): void {
  const hadSession = accessToken !== null;
  accessToken = null;
  expiresAt = 0;
  clearTimeout(renewTimer);
  if (hadSession) endListeners.forEach((listener) => listener());
}

/** Called when a running session ends unexpectedly (revoked, expired). */
export function onSessionEnd(listener: () => void): () => void {
  endListeners.add(listener);
  return () => endListeners.delete(listener);
}

/** Cognito user id (`sub`) of the signed-in user, or null. Read from the token payload. */
export function getCurrentUserId(): string | null {
  if (!accessToken) return null;
  try {
    const payload = accessToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const sub: unknown = JSON.parse(atob(payload)).sub;
    return typeof sub === "string" && sub.length > 0 ? sub : null;
  } catch {
    return null;
  }
}

/**
 * True when this browser has (or recently had) a session: the API sets a
 * readable `mdf_signed_in=1` cookie next to the HttpOnly session cookie.
 * Only used to skip the refresh call for visitors who never signed in; it
 * grants nothing by itself.
 */
export function hasSessionHint(): boolean {
  return document.cookie.split(";").some((cookie) => cookie.trim() === "mdf_signed_in=1");
}

/** A valid access token, renewed first when it is about to expire; null when signed out. */
export async function getAccessToken(): Promise<string | null> {
  if (accessToken && expiresAt - Date.now() > 30_000) return accessToken;
  return (await refreshSession()) ? accessToken : null;
}

/** New access token from the HttpOnly cookie. False when there is no valid session. */
export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(apiUrl("/v1/auth/refresh"), {
        method: "POST",
        credentials: "include",
        headers: SESSION_HEADERS,
      });
      if (!response.ok) {
        clearToken();
        return false;
      }
      setToken((await response.json()) as TokenResponse);
      return true;
    } catch {
      // Network problem: keep the current token; the next call tries again.
      return accessToken !== null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export interface StartLoginOptions {
  /** Open the sign-up form instead of the sign-in form. */
  signup?: boolean;
  /** Skip the login page and go straight to this provider (e.g. "Google"). */
  provider?: "Google";
  /** Where to land after login (path inside the app). */
  returnTo?: string;
}

/** Sends the browser to the Managed Login page (authorization code + PKCE). */
export async function startLogin(options: StartLoginOptions = {}, googleRetried = false) {
  const config = authConfig();
  const verifier = randomUrlSafe(48);
  const state = randomUrlSafe(16);
  const pending: PendingLogin = {
    verifier,
    state,
    returnTo: safeReturnTo(options.returnTo),
    googleRetried,
  };
  sessionStorage.setItem(PENDING_LOGIN_KEY, JSON.stringify(pending));

  const url = new URL(options.signup ? "/signup" : "/oauth2/authorize", config.domain);
  url.search = new URLSearchParams({
    client_id: config.clientId,
    response_type: "code",
    scope: config.scope,
    redirect_uri: config.redirectUri,
    state,
    code_challenge: await codeChallenge(verifier),
    code_challenge_method: "S256",
    ...(options.provider ? { identity_provider: options.provider } : {}),
  }).toString();
  window.location.assign(url.toString());
}

export type CallbackResult =
  | { kind: "signedIn"; returnTo: string }
  | { kind: "redirecting" }
  /** An admin disabled the account (Cognito: "User is not enabled"). */
  | { kind: "disabled" }
  | { kind: "error"; message: string };

/**
 * Cognito's wording for a disabled account. Only matched, never displayed:
 * the callback URL can be crafted by anyone.
 */
const ACCOUNT_DISABLED = /^User is (not enabled|disabled)\.?$/i;

/** Handles /auth/callback: trades the code for a session through our API. */
export async function completeLogin(params: URLSearchParams): Promise<CallbackResult> {
  const pending = readPendingLogin();
  const error = params.get("error");

  if (error) {
    const description = params.get("error_description") ?? "";
    // First Google sign-in of someone with an email account: the accounts were
    // just linked and Cognito asks for one more round trip. Retry once, silently.
    if (pending && !pending.googleRetried && LINKED_ON_FIRST_SIGN_IN.test(description)) {
      await startLogin({ provider: "Google", returnTo: pending.returnTo }, true);
      return { kind: "redirecting" };
    }
    sessionStorage.removeItem(PENDING_LOGIN_KEY);
    if (ACCOUNT_DISABLED.test(description)) return { kind: "disabled" };
    return {
      kind: "error",
      message: error === "access_denied" ? "Sign-in was cancelled." : "Sign-in failed. Please try again.",
    };
  }

  const code = params.get("code");
  if (!pending || !code || params.get("state") !== pending.state) {
    sessionStorage.removeItem(PENDING_LOGIN_KEY);
    return { kind: "error", message: "This sign-in link has expired. Please sign in again." };
  }
  sessionStorage.removeItem(PENDING_LOGIN_KEY);

  try {
    const response = await fetch(apiUrl("/v1/auth/session"), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...SESSION_HEADERS },
      body: JSON.stringify({
        code,
        codeVerifier: pending.verifier,
        redirectUri: authConfig().redirectUri,
      }),
    });
    if (!response.ok) throw new Error(`session ${response.status}`);
    setToken((await response.json()) as TokenResponse);
    return { kind: "signedIn", returnTo: pending.returnTo };
  } catch {
    return { kind: "error", message: "Sign-in failed. Please try again." };
  }
}

/** Ends the session everywhere: API cookie + Cognito's own login session. */
export async function logout(): Promise<void> {
  try {
    await fetch(apiUrl("/v1/auth/logout"), {
      method: "POST",
      credentials: "include",
      headers: SESSION_HEADERS,
    });
  } catch {
    // The Cognito logout below still ends the login session.
  }
  accessToken = null;
  expiresAt = 0;
  clearTimeout(renewTimer);
  const config = authConfig();
  const url = new URL("/logout", config.domain);
  url.search = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: config.logoutUri,
  }).toString();
  window.location.assign(url.toString());
}

function readPendingLogin(): PendingLogin | null {
  try {
    return JSON.parse(sessionStorage.getItem(PENDING_LOGIN_KEY) ?? "null") as PendingLogin | null;
  } catch {
    return null;
  }
}

/** Only same-app paths, never full URLs (no open redirects). */
function safeReturnTo(path: string | undefined): string {
  return path && path.startsWith("/") && !path.startsWith("//") ? path : "/";
}
